import { useState, RefObject } from 'react'
import { DEFAULT_DISPLAY_WIDTH } from '../constants'

export const useScaling = () => {
  const [originalWidth, setOriginalWidth] = useState<number | null>(null)
  const [originalHeight, setOriginalHeight] = useState<number | null>(null)
  const [imageScale, setImageScale] = useState(1)
  const [uiScale, setUiScale] = useState(1)
  const [textScale, setTextScale] = useState(1)
  const [imagePosition, setImagePosition] = useState({ top: 0, left: 0 })

  const calculateDynamicScale = (containerRef: RefObject<HTMLDivElement>) => {
    if (!containerRef.current || !originalWidth || !originalHeight) return 1

    const parentWidth =
      containerRef.current.parentElement?.offsetWidth || window.innerWidth
    const parentHeight =
      containerRef.current.parentElement?.offsetHeight || window.innerHeight

    // Calculate scale based on both width and height constraints
    const widthScale = parentWidth / originalWidth
    const heightScale = parentHeight / originalHeight

    // Use the smaller scale to ensure the image fits completely
    const scale = Math.min(widthScale, heightScale)

    return scale
  }

  const updateImagePosition = (ref: RefObject<HTMLDivElement>) => {
    if (!ref.current || !originalWidth || !originalHeight) return

    const containerWidth = ref.current.offsetWidth
    const containerHeight = ref.current.offsetHeight

    const scale = calculateDynamicScale(ref)

    // Calculate the actual rendered image dimensions
    const renderedWidth = originalWidth * scale
    const renderedHeight = originalHeight * scale

    // Calculate the position offset if the image doesn't take full width/height
    const leftOffset = (containerWidth - renderedWidth) / 2
    const topOffset = (containerHeight - renderedHeight) / 2

    setImagePosition({ top: topOffset, left: leftOffset })
  }

  const updateScales = (containerWidth: number) => {
    if (!originalWidth) return

    const newImageScale = containerWidth / originalWidth
    const newUiScale = containerWidth / DEFAULT_DISPLAY_WIDTH

    // More aggressive scaling for smaller screens
    const baseTextScale = Math.min(newUiScale, 0.8)
    const newTextScale = Math.min(baseTextScale, 1.2)

    setImageScale(newImageScale)
    setUiScale(newUiScale)
    setTextScale(newTextScale)
  }

  const adjustContainerToImageSize = (
    containerRef: RefObject<HTMLDivElement>
  ) => {
    if (!containerRef.current || !originalWidth || !originalHeight) return

    const scale = calculateDynamicScale(containerRef)

    // Calculate the actual rendered image dimensions
    const renderedWidth = originalWidth * scale
    const renderedHeight = originalHeight * scale

    // Set the container to exactly match the rendered image size
    containerRef.current.style.width = `${renderedWidth}px`
    containerRef.current.style.height = `${renderedHeight}px`

    // Reset position since we're exactly matching the image size
    setImagePosition({ top: 0, left: 0 })
  }

  return {
    originalWidth,
    originalHeight,
    imageScale,
    uiScale,
    textScale,
    imagePosition,
    calculateDynamicScale,
    setImageScale,
    setUiScale,
    setOriginalWidth,
    setOriginalHeight,
    updateScales,
    updateImagePosition,
    adjustContainerToImageSize,
  }
}
